import { DefaultPrivacyLevel, RumInitConfiguration } from "@datadog/browser-rum"
import type { BackOfficeSetupProps } from "@izettle/react-spa"

const errors: string[] = []

const envVar = <T extends string, D extends T | undefined = T>(
  value: string | undefined,
  defaultValue?: D,
): D extends undefined ? T | undefined : T => {
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  return (value || defaultValue) as any
}

const requiredEnvVar = <T extends string = string>(
  value: string | undefined,
  key: string,
) => {
  if (value == null) {
    errors.push(`Env variable \`${key}\` is required but was not found.`)
  }

  return value as T
}

const overrideWithEnvVar = (value: string, overrideEnvVar: string | undefined) => {
  // We have to use || since we want to override empty strings.
  // eslint-disable-next-line
  return overrideEnvVar || value
}

export enum Environment {
  Production = "production",
  Staging = "staging",
  Test = "test",
  Development = "development",
}

export type AppEnvironment =
  | Environment.Development
  | Environment.Staging
  | Environment.Production
export type NodeEnvironment =
  | Environment.Development
  | Environment.Test
  | Environment.Production

type Config = BackOfficeSetupProps["config"] & {
  appEnv: AppEnvironment
  nodeEnv: NodeEnvironment
  /** The Client header to set when calling keyval-service ([Name]-[Version]) */
  client: string

  /** The path the app will be presented on */
  publicPath: string

  translationsUrl: string
  portalUrl: string

  documentUpload: {
    url: string
  }
}

const appEnv = envVar<Config["appEnv"]>(process.env.APP_ENV, Environment.Staging)
const nodeEnv = envVar<Config["nodeEnv"]>(process.env.NODE_ENV, Environment.Development)

const getServiceUrl = (subdomain: string) =>
  `https://${subdomain}.${appEnv === "production" ? "zettle" : "zettletest"}.com`

const zettleOrIZettle = (input: string): string =>
  window.location.hostname.includes(".zettle")
    ? input.replace(".izettle", ".zettle")
    : input

// NOTE: `process.env.WHATEVER` HAS TO BE FULLY WRITTEN EACH TIME.
// Webpack's EnvironmentPlugin cannot replace usages of process.env[variable]
// with the correct values so we have to write it out fully each time.
export const config: Config = {
  appEnv,
  nodeEnv,
  client: `DocifyWeb-${process.env.GIT_SHA?.slice(0, 7)}`,

  publicPath: envVar(process.env.IZ_APP_PUBLIC_PATH, "/seller-verification"),
  translationsUrl: "https://lingo.zettle.com/v3/seller-verification",

  portalUrl: zettleOrIZettle(
    overrideWithEnvVar(getServiceUrl("my"), process.env.PORTAL_URL),
  ),

  sun: {
    url: overrideWithEnvVar(getServiceUrl("secure"), process.env.IZ_APP_SUN_URL),
  },

  login: {
    url: overrideWithEnvVar(getServiceUrl("login"), process.env.IZ_APP_LOGIN_URL),
  },

  oauth: {
    clientId: requiredEnvVar(
      process.env.IZ_APP_OAUTH_CLIENT_ID,
      "IZ_APP_OAUTH_CLIENT_ID",
    ),
    url: overrideWithEnvVar(getServiceUrl("oauth"), process.env.IZ_APP_OAUTH_URL),
  },

  documentUpload: {
    url: overrideWithEnvVar(
      `https://document-upload.${
        appEnv === "production" ? "izettle" : "izettletest"
      }.com`,
      process.env.IZ_APP_DOCUMENT_UPLOAD_URL,
    ),
  },
}

export const datadogRumConfig: RumInitConfiguration = {
  applicationId:
    appEnv === Environment.Production
      ? "f8b7e431-8804-4e92-8d85-ddf99e151fef"
      : "e2ec466f-75d8-4b43-b559-36cabdda0125",
  clientToken:
    appEnv === Environment.Production
      ? "pub5ce814f3c3fd5edad5c3b468d3100d93"
      : "pub2495ef4c913d855ccfab56749a3b24d9",
  service: "seller-verification",
  site: "datadoghq.eu",
  env: appEnv,
  version: config.client,
  sessionSampleRate: 100,
  sessionReplaySampleRate: 5,
  trackUserInteractions: true,
  trackResources: true,
  trackLongTasks: true,
  defaultPrivacyLevel: DefaultPrivacyLevel.MASK_USER_INPUT,
}

if (config.nodeEnv !== "test" && errors.length > 0) {
  throw new Error(`Invalid config!\n  • ${errors.join("\n  • ")}`)
}
